/* eslint-disable multiline-ternary */
import { CustomerAccount } from '@inyova/models';

import { Action, createReducer, createSelector, on } from '@ngrx/store';
import * as ReportingActions from './reporting.actions';
import * as AppRootActions from '@app/app.actions';
import * as ProfileActions from '@profile/profile.actions';
import * as fromAppRoot from '@app/app.reducers';
import { selectReportingFeature } from '@app/app.reducers';

import {
  GraphFilter,
  GraphFilterDays,
  GraphFilterSource,
  GraphFilterTimePeriod,
  Notification,
  ReportCurrentMonth,
  ReportItem,
  ReportItemGraph,
  ReportingState,
  TransactionType,
  TrasnactionFinal
} from '@shared/models/Reporting';
import { TranslateNumberPipe } from '@shared/pipes';
import { getStoredPerformanceFilter } from '@shared/utils';

export const REPORTING_REDUCER_FEATURE_KEY = 'reporting';

export function getReportingInitialState(): ReportingState {
  return {
    currentMonthData: {
      benchmark_return: 0,
      gross_return: 0,
      net_return: 0
    },
    data: [],
    filter: getStoredPerformanceFilter(),
    error: false,
    status: false,
    isInitialised: false,
    endValues: {
      benchmark_return: 0,
      deposits_value: 0,
      end_date: '',
      end_value: 0,
      fee_all: 0,
      first_month: '',
      first_year: 0,
      highest_return_net: 0,
      last_benchmark_date: '',
      lowest_return_net: 0,
      money_promotions: 0,
      monthly_investment: 0,
      profit_all: 0,
      reimbursement_all: 0,
      return_all_gross: 0,
      return_all_net: 0,
      simple_return_all_gross: 0,
      simple_return_all_net: 0,
      total_investment: 0,
      withdrawal_all: 0
    },
    transactions: [],
    customerNotifications: [],
    superhumanSurvey: {
      survey: [],
      canExitSurvey: undefined,
      showModal: undefined
    },
    transactionsPagination: {
      range: 16,
      currentPage: 1
    }
  };
}

export const initialState: ReportingState = getReportingInitialState();

const reportingReducer = createReducer(
  initialState,
  on(ReportingActions.getCurrentMonthSuccess, (state, action) => ({
    ...state,
    currentMonthData: action.data
  })),
  on(ReportingActions.getGraphDataSuccess, (state, action) => ({
    ...state,
    isInitialised: true,
    data: action.data
  })),
  on(ReportingActions.getGraphDataUnsuccess, (state, action) => ({
    ...state,
    isInitialised: false,
    error: action.error
  })),
  on(ReportingActions.fetchEndValuesStatus, (state, action) => ({
    ...state,
    status: action.value
  })),
  on(ReportingActions.updateSuperhumanExitSurvey, (state, action) => ({
    ...state,
    superhumanSurvey: {
      ...state.superhumanSurvey,
      canExitSurvey: action.value
    }
  })),
  on(ReportingActions.updateTransactionsPagination, (state, action) => ({
    ...state,
    transactionsPagination: {
      ...state.transactionsPagination,
      currentPage: action.currentPage
    }
  })),
  on(ReportingActions.showSuperhumanExitModal, (state, action) => ({
    ...state,
    superhumanSurvey: {
      ...state.superhumanSurvey,
      showModal: action.show
    }
  })),
  on(ReportingActions.fetchTransactionsSuccess, (state, action) => ({
    ...state,
    transactions: action.transactions
  })),
  on(ReportingActions.fetchEndValuesSuccess, (state, action) => ({
    ...state,
    endValues: action.endValues
  })),
  on(ReportingActions.changePerformanceFilter, (state, action) => ({
    ...state,
    filter: action.payload
  })),
  on(AppRootActions.switchAccount, ProfileActions.logout, () => getReportingInitialState()),
  on(ReportingActions.getCustomerNotificationsSuccess, (state, action) => ({
    ...state,
    customerNotifications: action.data
  })),
  on(ReportingActions.fetchSuperhumanSurveySuccess, (state, action) => ({
    ...state,
    superhumanSurvey: {
      ...state.superhumanSurvey,
      survey: action.survey
    }
  })),
  on(ReportingActions.fetchSuperhumanSurveyFailed, (state) => ({
    ...state,
    superhumanSurvey: {
      ...state.superhumanSurvey,
      survey: []
    }
  }))
);

export function reducer(state: ReportingState | undefined, action: Action): ReportingState {
  return reportingReducer(state, action);
}

/*
 * SELECTORS
 * */

export const selectEndValuesStatus = createSelector(selectReportingFeature, (state: ReportingState) => state.status);
export const selectIsInitialised = createSelector(selectReportingFeature, (state: ReportingState) => state.isInitialised);
export const selectEndValues = createSelector(selectReportingFeature, (state: ReportingState) => {
  return state.endValues;
});
export const selectAllTransactions = createSelector(selectReportingFeature, (state: ReportingState) => state.transactions);
export const selectTransactionsPagination = createSelector(selectReportingFeature, (state: ReportingState) => state.transactionsPagination);

export const selectTransactionsUpdated = createSelector(
  selectAllTransactions,
  selectTransactionsPagination,
  fromAppRoot.selectCustomerAndCurrentAccount,
  (state: ReportingState['transactions'], pagination, value) => {
    if (!value || !value.customer || !value.customer.email) {
      return [];
    }

    const transactions = [...state];
    const customData = [];

    transactions.forEach((element) => {
      const date = new Date(element.transaction_date);
      let translationKey: string;
      switch (element.transaction_type) {
        case TransactionType.DEPOSIT:
          translationKey = 'performance.detail.transfer.money_deposited';
          break;
        case TransactionType.REIMBURSEMENT:
          translationKey = 'performance.detail.transfer.reimbursement';
          break;
        case TransactionType.TRANSFER:
          translationKey = 'performance.detail.transfer.money_transfer';
          break;
        default:
          translationKey = 'performance.detail.transfer.money_withdrawn';
          break;
      }
      const amount = new TranslateNumberPipe().transform(element.transaction_value, value.customer.language, null, 2);

      const transaction = {
        date: date.toLocaleString(value.customer.language, { day: 'numeric', month: 'long', year: 'numeric' }),
        type: element.type_translated || translationKey,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        amount: element.transaction_value < 0 ? (amount as string).replace('-', '- ') : `+ ${amount}`
      };
      customData.push(transaction);
    });

    return customData as TrasnactionFinal[];
  }
);

export const selectTransactionsPagedInfo = createSelector(
  selectTransactionsUpdated,
  selectTransactionsPagination,
  (state: TrasnactionFinal[], pagination) => {
    const numberOfPages: number = Math.ceil(state.length / pagination.range);
    const endIndex: number = pagination.currentPage * pagination.range;
    const startIndex: number = endIndex - pagination.range;
    const { currentPage } = pagination;
    return { numberOfPages, startIndex, endIndex, currentPage };
  }
);

// Reporting selectors
export const selectFeatureData = createSelector(selectReportingFeature, (state: ReportingState) => state.data);
export const selectPerformanceFilter = createSelector(selectReportingFeature, (state: ReportingState) => state.filter);
export const selectGraphData = createSelector(selectFeatureData, selectPerformanceFilter, (state: ReportItem[], filter: GraphFilter) => {
  if (!state || !state.length) {
    return [];
  }
  const value = filter.source === GraphFilterSource.BALANCE ? 'end_value' : 'return';
  const dataFiltered = state.map((item) => {
    return {
      ...item,
      date: new Date(item.date),
      value: item[value]
    };
  });
  // Fix chart data which doesn't render 0 initial value
  if (dataFiltered[0].value === 0) {
    dataFiltered[0].value = 0.0000001;
    dataFiltered[0].return = 0.0000001;
  }
  return dataFiltered;
});
export const selectPerformanceGraphData = createSelector(
  selectGraphData,
  selectPerformanceFilter,
  (state: ReportItemGraph[], filter: GraphFilter) => {
    if (state && !state.length) {
      return state;
    }
    const selectedDate = state[state.length - 1].date;

    if (filter.timePeriod === GraphFilterTimePeriod.WEEK) {
      selectedDate.setDate(selectedDate.getDate() - GraphFilterDays.WEEK + 1);
    } else if (filter.timePeriod === GraphFilterTimePeriod.MONTH) {
      selectedDate.setDate(selectedDate.getDate() - GraphFilterDays.MONTH);
    } else if (filter.timePeriod === GraphFilterTimePeriod.QUARTER) {
      selectedDate.setDate(selectedDate.getDate() - GraphFilterDays.QUARTER);
    } else if (filter.timePeriod === GraphFilterTimePeriod.HALF_YEAR) {
      selectedDate.setDate(selectedDate.getDate() - GraphFilterDays.HALF_YEAR);
    } else if (filter.timePeriod === GraphFilterTimePeriod.YEAR) {
      selectedDate.setDate(selectedDate.getDate() - GraphFilterDays.YEAR);
    } else if (filter.timePeriod === GraphFilterTimePeriod.MORE_YEAR) {
      selectedDate.setDate(selectedDate.getDate() - GraphFilterDays.MORE_YEAR);
    } else {
      return state;
    }
    return state.filter((item) => item.date > selectedDate);
  }
);
export const selectTransactionsDays = createSelector(selectPerformanceGraphData, selectPerformanceFilter, (data, filter) => {
  if (filter.source === GraphFilterSource.PERFORMANCE || data.length > GraphFilterDays.YEAR) {
    return [];
  }
  return data.filter((item) => item.transactions.length);
});
export const selectTransactionsDepositTransfer = createSelector(selectTransactionsDays, (data) =>
  !data.length
    ? []
    : data.filter(
        (item) =>
          item.transactions[0].transaction_type === TransactionType.DEPOSIT || item.transactions[0].transaction_type === TransactionType.TRANSFER
      )
);
export const selectTransactionsWithdrawal = createSelector(selectTransactionsDays, (data) =>
  !data.length ? [] : data.filter((item) => item.transactions[0].transaction_type === TransactionType.WITHDRAWAL)
);

// Utils
function dateDiffInDays(a: Date, b: Date) {
  const MS_PER_DAY = 86400000;
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor((utc2 - utc1) / MS_PER_DAY);
}

export const selectDaysDifference = createSelector(selectFeatureData, (state) => {
  if (!(state && state.length)) {
    return 0;
  }
  const a = new Date(state[0].date);
  const b = new Date(state[state.length - 1].date);
  return dateDiffInDays(a, b);
});

export const selectCurrentMonth = createSelector(selectReportingFeature, (state: ReportingState) => state.currentMonthData);
export const selectCurrentMonthData = createSelector(selectCurrentMonth, (state: ReportCurrentMonth) => {
  if (!state) {
    return {};
  }
  return {
    benchmark_return: state.benchmark_return ? Number(state.benchmark_return.toFixed(1)) : 0,
    gross_return: state.gross_return ? Number(state.gross_return.toFixed(1)) : 0,
    net_return: state.net_return ? Number(state.net_return.toFixed(1)) : 0
  };
});

export const selectCustomerNotifications = createSelector(selectReportingFeature, (state) => state.customerNotifications);

export const selectRiskrecheckNotification = createSelector(
  selectCustomerNotifications,
  fromAppRoot.selectCurrentAccount,
  (customerNotifications: Notification[], account: CustomerAccount) => {
    return customerNotifications.filter(
      (notification) => notification.attributes?.data?.page === 'risk-recheck' && notification.attributes?.data?.account_id === account.id
    );
  }
);
export const selectSuperhumanNotification = createSelector(selectCustomerNotifications, (customerNotifications: Notification[]) => {
  return customerNotifications.filter((notification) => notification.attributes.data.page === 'superhuman');
});

export const selectSuperhuman = createSelector(selectReportingFeature, (state: ReportingState) => state.superhumanSurvey);
